import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["options", "defaultOption", "direction", "border", "optionBottomMargin", "optionRightMargin", "onClick", "dataTest", "hasBackground", "hasBottomMargin", "padding", "disabled"];
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './RadioList.styles';

var RadioList = function RadioList(_ref) {
  var options = _ref.options,
      defaultOption = _ref.defaultOption,
      direction = _ref.direction,
      border = _ref.border,
      optionBottomMargin = _ref.optionBottomMargin,
      optionRightMargin = _ref.optionRightMargin,
      onClick = _ref.onClick,
      dataTest = _ref.dataTest,
      hasBackground = _ref.hasBackground,
      hasBottomMargin = _ref.hasBottomMargin,
      padding = _ref.padding,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(defaultOption || options.length && options[0].id),
      _useState2 = _slicedToArray(_useState, 2),
      selected = _useState2[0],
      setSelected = _useState2[1];

  useEffect(function () {
    setSelected(defaultOption || options.length && options[0].id);
  }, [defaultOption]);

  var handleClick = function handleClick(id) {
    setSelected(id);
    onClick(id);
  };
  /* This is the native HTML radio button which is hidden but still functional */


  var buildNativeRadio = function buildNativeRadio(option, isDisabled) {
    var commonProps = {
      type: 'radio',
      name: 'radio-options',
      key: "RadioList-radio-".concat(option.id),
      id: "".concat(option.id, "-radio"),
      option: option.id,
      checked: selected === option.id,
      options: options,
      optionsCount: options.length,
      border: border,
      className: 'sr-only',
      hasBackground: hasBackground,
      onChange: function onChange() {
        return handleClick(option.id);
      }
    };
    return option.ariaLabel ? /*#__PURE__*/React.createElement(Styled.Radio, _extends({}, commonProps, {
      disabled: isDisabled,
      "aria-label": option.ariaLabel
    })) : /*#__PURE__*/React.createElement(Styled.Radio, _extends({}, commonProps, {
      disabled: isDisabled,
      "aria-labelledby": "".concat(option.id, "-label")
    }));
  };

  return /*#__PURE__*/React.createElement(Styled.RadioList, _extends({}, props, {
    hasBottomMargin: hasBottomMargin,
    "data-test": dataTest,
    "data-e2e": dataTest,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Styled.InnerContainer, null, /*#__PURE__*/React.createElement(Styled.FieldSet, null, /*#__PURE__*/React.createElement("legend", {
    className: "sr-only"
  }, "Radio Options"), /*#__PURE__*/React.createElement(Styled.ContentContainer, {
    direction: direction,
    id: "content-container",
    "data-test": "content-container",
    "data-e2e": "content-container"
  }, options.map(function (option) {
    return /*#__PURE__*/React.createElement(Styled.Label, {
      id: "".concat(option.id, "-label"),
      "data-e2e": option.dataTest ? "".concat(option.dataTest, "-link") : "".concat(option.id, "-link"),
      "data-test": option.dataTest ? "".concat(option.dataTest, "-link") : "".concat(option.id, "-link"),
      htmlFor: "".concat(option.id, "-radio"),
      key: "RadioList-label-".concat(option.id),
      optionBottomMargin: optionBottomMargin,
      optionRightMargin: optionRightMargin,
      direction: direction,
      "data-selected": selected === option.id
    }, buildNativeRadio(option, disabled || option.disabled), /*#__PURE__*/React.createElement("div", {
      className: "radioWrapper"
    }, /*#__PURE__*/React.createElement(Styled.RadioOption, {
      direction: direction,
      padding: padding,
      disabled: option.disabled
    }, /*#__PURE__*/React.createElement(Styled.Circle, null), /*#__PURE__*/React.createElement(Styled.LabelWrapper, null, option.label && /*#__PURE__*/React.createElement(Styled.LabelValue, {
      direction: direction
    }, option.label), option.description && /*#__PURE__*/React.createElement(Styled.LabelDescription, null, option.description)), option.rightHandElement && /*#__PURE__*/React.createElement(Styled.RightHandWrapper, {
      verticalAlign: option.rightHandElementAlign
    }, option.rightHandElement))));
  })))));
};

function labelOrDescriptionPropType(props, propName, componentName) {
  if (props.label && typeof props.label !== 'string') {
    throw new Error("Prop 'label' in '".concat(componentName, "' needs to be of type 'string'."));
  }

  if (props.description && _typeof(props.description) !== 'object') {
    throw new Error("Prop 'description' in '".concat(componentName, "' needs to be a valid JSX element."));
  }

  if (!props.label && !props.description) {
    throw new Error("One of props 'label' or 'description' was not specified in '".concat(componentName, "'."));
  }
}

RadioList.propTypes = {
  /**
   * Required prop for the options you wish to display.
   *
   * At least one of 'label' and 'description' is required.
   *
   * 'label' is of type 'string'.
   *
   * 'description' is of type 'element'.
   *
   * 'rightHandElement' is a React element and an optional prop.
   *
   * 'rightHandElementAlign' is of type 'string' and an optional prop which accepts the values: 'bottom', 'top' or 'middle' (default CSS if omitted).
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: labelOrDescriptionPropType,
    description: labelOrDescriptionPropType,
    id: PropTypes.string.isRequired,
    rightHandElement: PropTypes.element,
    rightHandElementAlign: PropTypes.oneOf(['bottom', 'middle', 'top']),
    dataTest: PropTypes.string,
    disabled: PropTypes.bool
  })).isRequired,

  /**
   * Optional prop to fire when an option is chosen.
   */
  onClick: PropTypes.func,

  /**
   * Optional prop to set which option is selected by default.
   *
   * Usage: pass in the ID of the option that you want to be selected by default. See prop 'options' for more information.
   *
   * If not passed in, the first option will be selected.
   */
  defaultOption: PropTypes.string,

  /**
   * Optional prop to set direction of radio buttons (stacked column or inline row) */
  direction: PropTypes.oneOf(['column', 'row']),

  /**
   * Optional prop to set whether borders should appear around the unselected options.
   *
   * If not passed in, it will be false.
   */
  border: PropTypes.bool,

  /**
   * Optional prop to set wether the option selected has a background colour
   *
   * If not passed in, the colour blue will be the default
   */
  hasBackground: PropTypes.bool,

  /**
   * Optional prop to set wether the component has a bottom margin
   *
   * If not passed in, the margin-bottom will have the default value of 50px
   */
  hasBottomMargin: PropTypes.bool,

  /**
   * Optional prop to set the padding
   *
   * If not passed in, the padding will have the default value of 15x20px
   */
  padding: PropTypes.string,

  /**
   * Optional prop to set how much vertical space should be in between each option.
   *
   * If not passed in, it will be "10px" by default.
   */
  optionBottomMargin: PropTypes.number,

  /**
   * Optional prop to set how much horizontal space should be in between each option.
   *
   * If not passed in, it will be "10px" by default.
   */
  optionRightMargin: PropTypes.number,

  /**
   * Specify an optional data-test attribute to be added for e2e testing.
   */
  dataTest: PropTypes.string,

  /**
   * Optional prop to set a disabled state on each option.
   *
   * If not passed in, it will be "false" by default.
   */
  disabled: PropTypes.bool
};
RadioList.defaultProps = {
  onClick:
  /* istanbul ignore next */
  function onClick(id) {
    // eslint-disable-next-line no-console
    console.log("Option clicked ==> ".concat(id));
  },
  defaultOption: '',
  direction: 'column',
  border: false,
  optionBottomMargin: 5,
  optionRightMargin: 5,
  hasBottomMargin: true,
  dataTest: 'radio-panel',
  hasBackground: true,
  padding: '15px 20px',
  disabled: false
};
export default RadioList;